/**
 * Homepage header fade and optional fancy scroll logo.
 * Config is injected via window.homepageHeaderConfig from sections/header.liquid.
 *
 * Logo phases (layered hero):
 *   1. at-rest      — absolute inside .holder (z-index 3), between background and foreground
 *   2. animating    — still in holder; top is holder-relative so motion looks viewport-fixed
 *   3. above-nav    — reparented to document.body, position fixed, z-index 6 (header is 5)
 *
 * Non-layered heroes (image/video) skip phase 1 and use fixed positioning throughout.
 *
 * Setting scroll distance and/or nav z-index threshold to 0 disables that aspect on
 * a breakpoint — the docked / above-nav state is applied on init without scrolling.
 *
 * Once elevated above the nav, the logo stays there until the page-top at-rest reset —
 * it is not demoted when scrollTop drops back below the nav threshold on the way up.
 */
(function () {
  'use strict';

  const END_WIDTH = 130;
  const Z_INDEX_NAV = 6;
  const SCROLL_START_THRESHOLD = 1;

  const BREAKPOINTS = {
    mobile: {
      endTop: 7.5,
      zIndexHero: 3,
    },
    desktop: {
      endTop: 10.5,
      zIndexHero: 3,
    },
  };

  const config = window.homepageHeaderConfig;
  if (!config) return;

  let logoEl;
  let headerWrapper;
  let layeredHolderRef = null;

  function initElements() {
    logoEl = document.getElementById('fancy-scroll-logo');
    headerWrapper = document.getElementById('header-wrapper');
  }

  function getHeroEl() {
    return document.querySelector('.homepage-hero');
  }

  function mountLogoInHero() {
    if (!logoEl || logoEl.dataset.mounted === 'true') return;

    const heroEl = getHeroEl();
    if (!heroEl) return;

    const holder = heroEl.querySelector('.holder');
    if (holder) {
      const foreground = holder.querySelector('.foreground');
      if (foreground) {
        holder.insertBefore(logoEl, foreground);
        logoEl.classList.add('in-layered-holder');
        layeredHolderRef = holder;
      } else {
        holder.appendChild(logoEl);
      }
    } else {
      heroEl.prepend(logoEl);
    }

    logoEl.dataset.mounted = 'true';
  }

  function getLayeredHolder() {
    if (!logoEl?.classList.contains('in-layered-holder')) return null;
    if (layeredHolderRef && logoEl.parentElement === layeredHolderRef) return layeredHolderRef;
    return null;
  }

  function elevateLogoAboveNav() {
    if (!logoEl || logoEl.classList.contains('above-nav')) return;

    const viewportTop = logoEl.getBoundingClientRect().top;
    document.body.appendChild(logoEl);
    logoEl.classList.add('above-nav');
    logoEl.style.top = `${viewportTop}px`;
    logoEl.style.removeProperty('z-index');
  }

  function restoreLogoToHero() {
    if (!logoEl || logoEl.parentElement !== document.body) return;

    const heroEl = getHeroEl();
    if (!heroEl) return;

    if (layeredHolderRef) {
      const foreground = layeredHolderRef.querySelector('.foreground');
      if (foreground) {
        layeredHolderRef.insertBefore(logoEl, foreground);
      } else {
        layeredHolderRef.appendChild(logoEl);
      }
      return;
    }

    heroEl.prepend(logoEl);
  }

  function setLogoTop(viewportTopPx) {
    const holder = getLayeredHolder();
    if (holder && !logoEl.classList.contains('above-nav')) {
      logoEl.style.top = `${viewportTopPx - holder.getBoundingClientRect().top}px`;
      return;
    }

    logoEl.style.top = `${viewportTopPx}px`;
  }

  function getBreakpointConfig() {
    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    const bp = isMobile ? 'mobile' : 'desktop';
    return { ...config[bp], ...BREAKPOINTS[bp], endWidth: END_WIDTH, zIndexNav: Z_INDEX_NAV };
  }

  function lerp(start, end, progress) {
    return start + (end - start) * progress;
  }

  function getStartMetrics(cfg) {
    const viewportWidth = window.innerWidth;
    return {
      widthPx: (viewportWidth * cfg.startWidth) / 100,
      topPx: (viewportWidth * cfg.startTopVw) / 100,
    };
  }

  function shouldUseAtRestState(scrollTop, cfg) {
    if (scrollTop >= SCROLL_START_THRESHOLD) return false;
    if (cfg.scrollDistance <= 0) return false;
    if (cfg.navZIndexThreshold <= 0) return false;
    return true;
  }

  function getScrollProgress(scrollTop, distance) {
    if (distance <= 0) return 1;
    return Math.min(Math.max(scrollTop / distance, 0), 1);
  }

  function clearLogoInlineStyles() {
    if (!logoEl) return;
    restoreLogoToHero();
    logoEl.classList.remove('scrolled-past', 'above-nav');
    logoEl.style.removeProperty('width');
    logoEl.style.removeProperty('top');
    logoEl.style.removeProperty('z-index');
  }

  function updateLogoZIndex(scrollTop, cfg) {
    if (!logoEl) return;

    const shouldElevate = scrollTop >= cfg.navZIndexThreshold || cfg.scrollDistance <= 0;

    if (shouldElevate || logoEl.classList.contains('above-nav')) {
      elevateLogoAboveNav();
      return;
    }

    if (scrollTop >= SCROLL_START_THRESHOLD) {
      logoEl.style.zIndex = String(cfg.zIndexHero);
    } else {
      logoEl.style.removeProperty('z-index');
    }
  }

  function updateHeaderFade(scrollTop) {
    if (!headerWrapper) return;

    const header = document.querySelector('.header');
    if (header && header.classList.contains('menu-open')) return;

    const heroEl = getHeroEl();
    const heroHeight = heroEl ? heroEl.offsetHeight : window.innerHeight;
    const fadeStart = heroHeight * config.headerFadeStartRatio;
    let opacity = 0;

    if (scrollTop > fadeStart) {
      opacity = ((scrollTop - fadeStart) / config.headerFadeDistance) * config.headerFadeSpeed;
      if (opacity > 1) opacity = 1;
    }

    headerWrapper.style.setProperty('background-color', `rgba(230,230,230,${opacity})`, 'important');
    headerWrapper.style.setProperty('color', '#FFFFFF', 'important');
  }

  function applyEndState(cfg) {
    if (!logoEl) return;
    logoEl.classList.add('scrolled-past');
    logoEl.style.removeProperty('width');

    if (getLayeredHolder() && !logoEl.classList.contains('above-nav')) {
      setLogoTop(cfg.endTop);
    } else {
      logoEl.style.removeProperty('top');
    }
  }

  function applyAnimatedState(scrollTop, cfg) {
    if (!logoEl) return;

    const progress = getScrollProgress(scrollTop, cfg.scrollDistance);

    updateLogoZIndex(scrollTop, cfg);

    if (progress >= 1) {
      applyEndState(cfg);
      return;
    }

    logoEl.classList.remove('scrolled-past');

    const { widthPx: startWidthPx, topPx: startTopPx } = getStartMetrics(cfg);
    const widthPx = lerp(startWidthPx, cfg.endWidth, progress);
    const topPx = lerp(startTopPx, cfg.endTop, progress);

    logoEl.style.width = `${widthPx}px`;
    setLogoTop(topPx);
  }

  function updateFancyLogo(scrollTop) {
    if (!config.fancyLogo || !logoEl) return;

    const cfg = getBreakpointConfig();

    if (shouldUseAtRestState(scrollTop, cfg)) {
      clearLogoInlineStyles();
      return;
    }

    applyAnimatedState(scrollTop, cfg);
  }

  function onScroll() {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    updateFancyLogo(scrollTop);
    updateHeaderFade(scrollTop);
  }

  function syncLayeredLogoAtRest() {
    if (!logoEl?.classList.contains('in-layered-holder')) return;
    if (logoEl.classList.contains('above-nav') || logoEl.classList.contains('scrolled-past')) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const cfg = getBreakpointConfig();
    if (!shouldUseAtRestState(scrollTop, cfg)) return;

    logoEl.style.removeProperty('top');
  }

  function init() {
    initElements();
    mountLogoInHero();
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.matchMedia('(max-width: 989px)').addEventListener('change', onScroll);
  window.addEventListener('resize', () => {
    syncLayeredLogoAtRest();
    onScroll();
  }, { passive: true });
})();
